import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Phone, MessageCircle, AlertTriangle } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogPerditaAcquaCosaFare = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Perdita Acqua Cosa Fare a Roma - Guida Urgente | SOS Casa Roma</title>
        <meta name="description" content="Perdita d'acqua in casa a Roma? Scopri cosa fare immediatamente. Chiamaci H24 per intervento urgente entro 60 minuti. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/perdita-acqua-cosa-fare-roma`} />
      </Helmet>
      
      <div className="min-h-screen">
        <Header />
        
        <article className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-red-100 border-l-4 border-red-600 p-4 mb-6">
              <div className="flex items-center">
                <AlertTriangle className="text-red-600 mr-2" />
                <p className="font-bold text-red-800">EMERGENZA? Chiamaci subito: 328 768 6356</p>
              </div>
            </div>

            <h1 className="text-4xl font-bold mb-6 text-gray-900">
              Perdita d'Acqua in Casa a Roma: Cosa Fare Subito
            </h1>
            
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                Hai una <strong>perdita d'acqua urgente</strong> in casa a Roma? Non farti prendere dal panico. 
                Segui questi 3 passi immediati:
              </p>

              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">1️⃣ Chiudi Subito l'Acqua</h2>
                <p className="text-gray-700 mb-2">
                  Trova il rubinetto generale (di solito sotto il lavandino della cucina o nel bagno) e chiudilo completamente.
                </p>
                <p className="text-sm text-gray-600">Questo ferma immediatamente l'allagamento e previene danni maggiori.</p>
              </div>

              <div className="bg-orange-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">2️⃣ Asciuga l'Acqua Visibile</h2>
                <p className="text-gray-700 mb-2">
                  Usa stracci, asciugamani o un aspiraliquidi per rimuovere l'acqua dal pavimento.
                </p>
                <p className="text-sm text-gray-600">Previeni danni a parquet, mobili e infiltrazioni ai piani inferiori.</p>
              </div>

              <div className="bg-red-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">3️⃣ Chiama Immediatamente un Idraulico</h2>
                <p className="text-gray-700 mb-4">
                  <strong>Non aspettare.</strong> Una perdita anche piccola può causare danni enormi in poche ore.
                </p>
                <div className="bg-white rounded-lg p-4 border-2 border-red-500">
                  <p className="font-bold text-red-800 mb-2">☎️ SOS Casa Roma - Intervento Urgente</p>
                  <p className="text-gray-700 mb-2">✅ Arriviamo entro 60 minuti a Roma</p>
                  <p className="text-gray-700 mb-4">✅ Disponibili H24, anche di notte e festivi</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="tel:3287686356" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <Phone className="mr-2 h-5 w-5" />
                      CHIAMA ORA: 328 768 6356
                    </a>
                    <a href="https://wa.me/393287686356" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      SCRIVI SU WHATSAPP
                    </a>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Tipi di Perdita Comuni a Roma:</h3>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li className="mb-2"><strong>Tubo scoppiato:</strong> Emergenza massima, chiama subito</li>
                <li className="mb-2"><strong>Rubinetto che perde:</strong> Sembra piccolo ma spreca 30L al giorno</li>
                <li className="mb-2"><strong>WC che perde:</strong> Spreco continuo e aumento bolletta</li>
                <li className="mb-2"><strong>Perdita nascosta nel muro:</strong> Serve ricerca perdite professionale</li>
              </ul>

              <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">⚠️ Quando è URGENTE chiamare:</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Acqua che esce abbondante da un tubo</li>
                  <li>Allagamento in corso</li>
                  <li>Perdita vicino a quadro elettrico</li>
                  <li>Acqua che scende dal soffitto</li>
                </ul>
                <p className="mt-4 font-bold text-red-700">In questi casi NON ASPETTARE. Chiamaci immediatamente.</p>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Quanto Costa un Intervento Urgente a Roma?</h3>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700 mb-2">• <strong>Uscita tecnico:</strong> 40€</p>
                <p className="text-gray-700 mb-2">• <strong>Intervento entro 1 ora:</strong> 60€</p>
                <p className="text-gray-700">• <strong>Emergenza notturna:</strong> da 80€</p>
              </div>

              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Hai una Perdita Adesso?</h3>
                <p className="text-lg text-gray-700 mb-4">Non perdere tempo. Ogni minuto conta.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:3287686356" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <Phone className="mr-2 h-6 w-6" />
                      CHIAMA ORA: 328 768 6356
                    </a>
                  <a href="https://wa.me/393287686356" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <MessageCircle className="mr-2 h-6 w-6" />
                      WHATSAPP IMMEDIATO
                    </a>
                </div>
                <p className="text-sm text-gray-600 mt-4">✅ Arriviamo entro 60 minuti • ✅ Tecnici qualificati • ✅ Prezzi chiari</p>
              </div>
            </div>
          </div>
        </article>
        
        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogPerditaAcquaCosaFare;